// District building generation — data-driven port of v1
// js/world-manager.js:1396-1595 (createDistrictSpecificBlocks /
// createDistrictBuilding). Sizes, spacing and style colors are v1's;
// windows are one InstancedMesh per building (v1 batched per face).

import * as THREE from 'three';
import {
  BUILDING_STYLES,
  type DistrictDef,
  type BuildingType,
  type StyleName,
} from '@/content/districts';
import type { Rng } from '@/core/rng';
import { InstancedSet } from '@/render/instanced-set';
import type { WorldTextures } from '@/render/textures';

const TILE_LENGTH = 80;

// Shared facade-texture clones (one GL texture per source, fixed repeat)
const facadeCache = new WeakMap<THREE.Texture, THREE.Texture>();
function facadeMap(texture: THREE.Texture | null): THREE.Texture | null {
  if (!texture) return null;
  let clone = facadeCache.get(texture);
  if (!clone) {
    clone = texture.clone();
    clone.repeat.set(2, 3);
    clone.needsUpdate = true;
    facadeCache.set(texture, clone);
  }
  return clone;
}

function facadeTexture(style: StyleName, textures: WorldTextures): THREE.Texture | null {
  switch (style) {
    case 'gründerzeit':
      return facadeMap(textures.brickRed);
    case 'industrial':
    case 'postwar':
      return facadeMap(textures.brickDark);
    default:
      return facadeMap(textures.wallBright);
  }
}

const sharedWindowGeometry = new THREE.BoxGeometry(0.32, 0.45, 0.04);
const dayWindowMaterial = new THREE.MeshLambertMaterial({ color: 0x35455e });

/** Night: windows glow warm (v1 batchedWindows lighting); day: dark glass. */
export function setWindowsNight(lit: boolean): void {
  dayWindowMaterial.emissive.setHex(lit ? 0xdd9944 : 0x000000);
  dayWindowMaterial.color.setHex(lit ? 0x554422 : 0x35455e);
}

interface BuildingDims {
  width: number;
  height: number;
  depth: number;
}

/** v1 building dimensions per type (js/world-manager.js:1496-1527). */
function rollDimensions(type: BuildingType, district: DistrictDef, rng: Rng): BuildingDims {
  const [minH, maxH] = district.buildings.heightRange;
  const tallChance = district.buildings.tallBuildingChance ?? 1.0;
  const allowTall = rng.next() < tallChance;

  if (type === 'family' || type === 'villa') {
    return {
      width: rng.next() * 3 + 2.5,
      height: allowTall ? (minH + rng.next() * (maxH - minH)) * 1.5 : minH * 1.2 + rng.next() * 0.5,
      depth: rng.next() * 2.5 + 2.5,
    };
  }
  if (type === 'office') {
    return {
      width: rng.next() * 1.5 + 2,
      height: allowTall ? (minH + rng.next() * (maxH - minH)) * 2 : minH * 1.5 + rng.next() * 0.8,
      depth: rng.next() * 1.5 + 2,
    };
  }
  return {
    width: rng.next() * 2 + 1.5,
    height: allowTall ? (minH + rng.next() * (maxH - minH)) * 1.8 : minH * 1.3 + rng.next() * 0.7,
    depth: rng.next() * 2 + 2,
  };
}

function pick<T>(arr: readonly T[], rng: Rng): T {
  const item = arr[Math.floor(rng.next() * arr.length)];
  if (item === undefined) throw new Error('pick from empty array');
  return item;
}

/** Street-facing instanced windows for one building. */
function addWindows(
  group: THREE.Group,
  dims: BuildingDims,
  position: THREE.Vector3,
  side: number,
  rng: Rng,
): void {
  const floors = Math.max(1, Math.floor(dims.height / 1.1));
  const columns = Math.max(1, Math.floor(dims.depth / 0.8));
  const set = new InstancedSet(sharedWindowGeometry, dayWindowMaterial, floors * columns);
  // Face toward the street (negative-x side of the building for side=1)
  const faceX = position.x - side * (dims.width / 2 + 0.03);
  for (let f = 0; f < floors; f++) {
    for (let c = 0; c < columns; c++) {
      if (rng.next() < 0.15) continue; // Some windowless spots
      const y = position.y - dims.height / 2 + 0.7 + f * 1.1;
      const z = position.z - dims.depth / 2 + 0.5 + c * 0.8;
      set.add(faceX, y, z, 1, Math.PI / 2);
    }
  }
  set.mesh.userData.noToon = true;
  group.add(set.mesh);
}

/** Populate one 80-unit tile with buildings for the district.
 *  Local z range is [-TILE_LENGTH, 0). Returns meshes added to `tile`. */
export function buildBuildingsIntoTile(
  tile: THREE.Group,
  district: DistrictDef,
  rng: Rng,
  textures: WorldTextures,
): void {
  const cfg = district.buildings;
  const density = district.buildingDensity;
  const spacingMultiplier =
    cfg.spacing === 'sparse'
      ? 2.5
      : cfg.spacing === 'dense'
        ? 0.8
        : cfg.spacing === 'packed'
          ? 0.5
          : 1.2;
  const buildingSpacing =
    cfg.spacing === 'sparse' ? 5 : cfg.spacing === 'dense' ? 2 : cfg.spacing === 'packed' ? 0.1 : 3;
  const baseBuildingsInBlock =
    cfg.spacing === 'sparse' ? 1 : cfg.spacing === 'dense' ? 3 : cfg.spacing === 'packed' ? 4 : 2;
  const isSuburban = cfg.spacing === 'sparse';
  const performanceMultiplier = isSuburban ? 0.7 : 1.0;

  // Fill the tile with blocks every 20*spacingMultiplier units (v1 cadence)
  const blockPitch = 20 * spacingMultiplier;
  const totalBlocks = Math.max(1, Math.ceil((TILE_LENGTH / blockPitch) * performanceMultiplier));

  for (let blockIndex = 0; blockIndex < totalBlocks; blockIndex++) {
    for (const side of [-1, 1]) {
      const blockStartZ = -blockIndex * blockPitch - 4;
      const buildingsInBlock = Math.ceil(
        (baseBuildingsInBlock + Math.floor(rng.next() * 2)) * density,
      );

      for (let i = 0; i < buildingsInBlock; i++) {
        const styleName = pick(cfg.predominantStyles, rng);
        const style = BUILDING_STYLES[styleName];
        const type = pick(cfg.types, rng);
        const dims = rollDimensions(type, district, rng);

        let x: number;
        let z: number;
        if (cfg.spacing === 'packed') {
          // Adjacent façades along x (v1 :1548-1553)
          x = side * (8 + i * (dims.width + buildingSpacing) * 0.35);
          z = blockStartZ - i * 2 * spacingMultiplier;
        } else {
          x = side * (8 + i * buildingSpacing);
          z = blockStartZ - i * 4 * spacingMultiplier;
        }
        if (z < -TILE_LENGTH + dims.depth / 2) continue; // Keep inside the tile

        const color = pick(style.colors, rng);
        const map = facadeTexture(styleName, textures);
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(dims.width, dims.height, dims.depth),
          new THREE.MeshLambertMaterial({ color, ...(map ? { map } : {}) }),
        );
        const position = new THREE.Vector3(x, dims.height / 2 - 0.05, z);
        body.position.copy(position);
        body.castShadow = false;
        body.receiveShadow = true;
        tile.add(body);

        const roof = new THREE.Mesh(
          new THREE.BoxGeometry(dims.width + 0.2, 0.3, dims.depth + 0.2),
          new THREE.MeshLambertMaterial({ color: style.roofColor }),
        );
        roof.position.set(x, position.y + dims.height / 2 + 0.15, z);
        tile.add(roof);

        addWindows(tile, dims, position, side, rng);

        // Suburban garden patch next to family homes (simplified v1 createGarden)
        if ((type === 'family' || type === 'villa') && rng.next() < cfg.gardenChance * 0.6) {
          const garden = new THREE.Mesh(
            new THREE.BoxGeometry(dims.width + 2, 0.06, dims.depth + 2),
            new THREE.MeshLambertMaterial({ color: 0x2e8b2e }),
          );
          garden.position.set(x, 0.01, z);
          tile.add(garden);
        }
      }
    }
  }
}
